/**
 * Preseed bulk-action gating — the handler-level refusal.
 *
 * The preseed tab has no bulk endpoint wiring: the backend bulk entity
 * allowlist excludes `preseed`, so a preseed approve/reject fans out one PUT
 * per *loaded* row. Two ways a selection outgrows that fan-out:
 *
 * - The Gmail-style banner promotes the selection to `'all'` mode ("Select all
 *   N matching") and the handlers still only see the loaded page — so they
 *   mutate a page and report success for all N. ENG-5389.
 * - The selection simply passes the bulk threshold in `'manual'` mode, where
 *   every other entity switches to `POST /bulk/{resource}`. Preseeds cannot, so
 *   what would be one bounded job stays an unbounded `Promise.allSettled`
 *   fan-out. ENG-5427.
 *
 * This module is the handler-level half of that refusal. Its only production
 * consumer is `hooks/actions/useSeedsActionHandlers.ts`, which calls it at the
 * top of the preseed branch of `handleApprove` and `handleReject`.
 *
 * The UI-level gate is a *separate* predicate and deliberately NOT a consumer
 * of this module: `sections/seeds/index.tsx` computes
 * `enableSelectAllMatching = activeQuery != null && type !== 'preseed'`, which
 * tests entity *identity* (which tab is mounted), whereas this module tests
 * selection *mode* and *size*. Different questions over different inputs —
 * there is no single source to share between them.
 *
 * The count arm is what makes this refusal REACHABLE through the app. The UI
 * gate removes the only route into `'all'` mode on the preseed tab, so the mode
 * arm alone was dead code kept as defense in depth; nothing, however, stops a
 * user from ticking three preseed rows by hand. That selection used to fan out
 * silently and now gets refused — a deliberate, user-visible behavior change
 * (ENG-5427 AC4).
 *
 * Refuse, never reroute: there is no `/bulk` route for preseeds to reroute to.
 * And the caller keeps this call INSIDE its preseed branch — hoisting it would
 * refuse regular-seed bulk actions, which do have a working bulk endpoint.
 *
 * Deliberately pure: no React import, type-level import only, so it is
 * trivially unit-testable.
 */

import { BULK_THRESHOLD } from '@/hooks/useBulkAction/useBulkAction';
import type { BulkSelectionState } from '@/hooks/useBulkSelection/types';

/**
 * Returns the refusal copy when a preseed status mutation has outgrown the
 * per-row fan-out — the selection is in `'all'` mode, or it exceeds
 * `BULK_THRESHOLD` — and `null` when the mutation may proceed.
 *
 * `BULK_THRESHOLD` is imported, never re-derived as a literal, and the
 * comparison is a STRICT `>` to mirror `shouldUseBulk` exactly: refusal begins
 * at the first count that would otherwise have taken the bulk endpoint, i.e. 3
 * rows.
 *
 * `selectionState` is optional on `SeedsActionHandlerConfig` and fails OPEN
 * here: the single-row drawer path never wires bulk selection, and refusing it
 * would be a regression. The `selectedCount` arm still applies, so a stateless
 * caller handing over a large selection is refused on the count alone.
 */
export function getPreseedBulkRefusal(
  selectedCount: number,
  selectionState?: Pick<BulkSelectionState, 'mode'>
): string | null {
  if (selectionState?.mode === 'all' || selectedCount > BULK_THRESHOLD) {
    return `Bulk approve and reject are not available for preseeds — select ${BULK_THRESHOLD} or fewer at a time.`;
  }
  return null;
}
